import { useState, useEffect, useRef, Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectCanCreateGreenInvoice,
  selectIsDigitalInvoiceActive,
  selectCanAccessFinance,
  selectHasPermission,
} from "../../../store/slices/authSlice";
import {
  useUpdateAppointmentMutation,
  calendarApi,
} from "../../../store/services/calendarApi";
import { useCreateTaskMutation } from "../../../store/services/tasksApi";
import {
  useUpdateTransactionMutation,
  useUpdateTransactionStatusMutation,
  financeApi,
} from "../../../store/services/financeApi";
import {
  getTransactionByAppointment,
  TRANSACTION_STATUS,
} from "../../../firebase";
import {
  Check,
  X,
  CurrencyCircleDollar,
  NotePencil,
  Clock,
  Warning,
  Receipt,
  CalendarBlank,
} from "@phosphor-icons/react";
import InvoiceQuickModal from "../../morning/InvoiceQuickModal";
import DigitalInvoiceQuickModal from "../../digitalInvoice/DigitalInvoiceQuickModal";
import DatePicker from "../../ui/DatePicker";
import useGenderText from "./useGenderText";
import LoaderLoop from "../../shared/loader/LoaderLoop";
import "./CatchUpMode.css";
import { getIsraelDateString } from "../../../utils/dateUtils";

// Step identifiers - same as original
const STEPS = {
  APPROVAL: "approval",
  APPROVAL_CANCEL_CONFIRM: "approval_cancel_confirm",
  CANCELLATION_PAYMENT_ASK: "cancellation_payment_ask",
  CANCELLATION_PAYMENT_AMOUNT: "cancellation_payment_amount",
  CANCELLATION_PAYMENT_DETAILS: "cancellation_payment_details",
  CANCELLATION_PAYMENT_INVOICE: "cancellation_payment_invoice",
  PAYMENT: "payment",
  PAYMENT_DETAILS: "payment_details",
  PAYMENT_INVOICE: "payment_invoice",
  PAYMENT_TASK: "payment_task",
  SUMMARY: "summary",
  SUMMARY_REMINDER: "summary_reminder",
};

// Payment methods
const PAYMENT_METHODS = [
  { value: "cash", label: "מזומן" },
  { value: "credit", label: "אשראי" },
  { value: "transfer", label: "העברה" },
  { value: "bit", label: "אפליקציה", subLabel: "ביט/פייבוקס/אחר" },
  { value: "check", label: "צ׳ק" },
  { value: "other", label: "אחר" },
];

const INITIAL_CARD_STATE = {
  currentStep: null,
  paymentAmount: null,
  paymentMethod: "cash",
  paymentDate: null,
  isLoading: false,
};

export default function PendingActionCard({ appointment, onResolved }) {
  const apt = appointment;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const g = useGenderText();

  const isUsingGreenInvoice = useSelector(selectCanCreateGreenInvoice);
  const isDigitalInvoiceActive = useSelector(selectIsDigitalInvoiceActive);
  const canAccessFinance = useSelector(selectCanAccessFinance);
  // treatments.write === false hides the "write summary" CTA (legacy therapists resolve TRUE)
  const canWriteTreatments = useSelector(selectHasPermission("treatments.write"));

  // Local state scoped to this single appointment
  const [cardState, setCardState] = useState(INITIAL_CARD_STATE);

  // Invoice modal (scoped to this card)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceAppointment, setInvoiceAppointment] = useState(null);
  const [invoicePaidAmount, setInvoicePaidAmount] = useState(null);

  // Guard so onResolved fires only once
  const resolvedRef = useRef(false);

  // Transient "pop" highlight for a step that just became done (eye-catch ~400ms)
  const [poppingSteps, setPoppingSteps] = useState({});
  const prevDoneRef = useRef(null);

  // Mutations
  const [updateAppointment] = useUpdateAppointmentMutation();
  const [createTask] = useCreateTaskMutation();
  const [updateTransaction] = useUpdateTransactionMutation();
  const [updateTransactionStatus] = useUpdateTransactionStatusMutation();

  // Invalidate finance tags
  const invalidateAllFinanceTags = (patientId) => {
    dispatch(
      calendarApi.util.invalidateTags([
        { type: "Finance", id: "STATS" },
        { type: "Finance", id: "PENDING_PAYMENTS" },
        ...(patientId ? [{ type: "Finance", id: `PATIENT_${patientId}` }] : []),
      ])
    );
    dispatch(
      financeApi.util.invalidateTags([
        { type: "TransactionStats", id: "MONTHLY" },
        { type: "PendingCollection", id: "LIST" },
        ...(patientId
          ? [{ type: "Transactions", id: `PATIENT_${patientId}` }]
          : []),
      ])
    );
  };

  // Helpers
  const formatTime = (date) =>
    new Date(date).toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("he-IL", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });

  // State accessors (scoped to single card)
  const getCardState = () => cardState;

  const updateCardState = (updates) => {
    setCardState((prev) => ({ ...prev, ...updates }));
  };

  // Get current step based on NotifyAbout flags (same logic as original)
  const getCurrentStep = (apt, cardState) => {
    // Sub-steps take priority
    if (cardState?.currentStep === STEPS.APPROVAL_CANCEL_CONFIRM)
      return STEPS.APPROVAL_CANCEL_CONFIRM;
    if (cardState?.currentStep === STEPS.CANCELLATION_PAYMENT_ASK)
      return STEPS.CANCELLATION_PAYMENT_ASK;
    if (cardState?.currentStep === STEPS.CANCELLATION_PAYMENT_AMOUNT)
      return STEPS.CANCELLATION_PAYMENT_AMOUNT;
    if (cardState?.currentStep === STEPS.CANCELLATION_PAYMENT_DETAILS)
      return STEPS.CANCELLATION_PAYMENT_DETAILS;
    if (cardState?.currentStep === STEPS.CANCELLATION_PAYMENT_INVOICE)
      return STEPS.CANCELLATION_PAYMENT_INVOICE;
    if (cardState?.currentStep === STEPS.PAYMENT_DETAILS)
      return canAccessFinance ? STEPS.PAYMENT_DETAILS : null;
    if (cardState?.currentStep === STEPS.PAYMENT_TASK)
      return canAccessFinance ? STEPS.PAYMENT_TASK : null;
    if (cardState?.currentStep === STEPS.PAYMENT_INVOICE)
      return canAccessFinance ? STEPS.PAYMENT_INVOICE : null;
    if (cardState?.currentStep === STEPS.SUMMARY_REMINDER)
      return STEPS.SUMMARY_REMINDER;

    // Manual step selection
    if (
      cardState?.currentStep === STEPS.APPROVAL &&
      apt.NotifyAboutThisMeetingApproval === true
    )
      return STEPS.APPROVAL;
    if (
      cardState?.currentStep === STEPS.SUMMARY &&
      apt.NotifyAboutThisMeetingSummarize === true &&
      apt.isTempCustomer !== true
    )
      return STEPS.SUMMARY;
    if (
      cardState?.currentStep === STEPS.PAYMENT &&
      apt.NotifyAboutThisMeetingPaymentStatus === true
    )
      return canAccessFinance ? STEPS.PAYMENT : null;

    // Auto-determine by flags
    // Temp customers (לקוח מזדמן) have no patient record — no summary step
    if (apt.NotifyAboutThisMeetingApproval === true) return STEPS.APPROVAL;
    if (
      apt.NotifyAboutThisMeetingSummarize === true &&
      apt.isTempCustomer !== true
    )
      return STEPS.SUMMARY;
    if (apt.NotifyAboutThisMeetingPaymentStatus === true)
      return canAccessFinance ? STEPS.PAYMENT : null;

    return null;
  };

  // Signal resolution once when there are no remaining pending steps
  const markResolved = () => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    if (onResolved) onResolved(apt.id);
  };

  const currentStep = getCurrentStep(apt, cardState);

  // When the appointment has no remaining pending steps, signal resolution.
  // A sub-step in progress (e.g. cancellation payment flow) keeps currentStep
  // truthy, so this only fires once the flow has fully completed.
  useEffect(() => {
    if (cardState.isLoading) return;
    if (currentStep !== null) return;
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    if (onResolved) onResolved(apt.id);
  }, [currentStep, cardState.isLoading, onResolved, apt.id]);

  // Pop the stepper item that *just* transitioned to "done" so the eye catches
  // the status change. Skips the very first render (no pop on open).
  useEffect(() => {
    const curDone = {
      approval: apt.isApprovedByTherapist === true,
      summary: apt.isTherapistSummarizedTheMeeting === true,
      payment: apt.isTherapistUpdatedPaymentStatus === true,
    };
    if (prevDoneRef.current === null) {
      prevDoneRef.current = curDone;
      return;
    }
    const newlyDone = Object.keys(curDone).filter(
      (k) => curDone[k] && !prevDoneRef.current[k]
    );
    prevDoneRef.current = curDone;
    if (newlyDone.length === 0) return;

    setPoppingSteps((prev) => {
      const next = { ...prev };
      newlyDone.forEach((k) => { next[k] = true; });
      return next;
    });
    const timer = setTimeout(() => {
      setPoppingSteps((prev) => {
        const next = { ...prev };
        newlyDone.forEach((k) => { delete next[k]; });
        return next;
      });
    }, 450);
    return () => clearTimeout(timer);
  }, [
    apt.isApprovedByTherapist,
    apt.isTherapistSummarizedTheMeeting,
    apt.isTherapistUpdatedPaymentStatus,
  ]);

  // ==========================================
  // ACTION HANDLERS (same as original)
  // ==========================================

  // Approval: Yes
  const handleApprovalYes = async (apt) => {
    updateCardState({ isLoading: true });
    try {
      await updateAppointment({
        clinicId,
        appointmentId: apt.id,
        appointmentData: {
          isApprovedByTherapist: true,
          NotifyAboutThisMeetingApproval: false,
        },
        therapistId,
        therapistName,
        patientName: apt.patientName,
      }).unwrap();

      try {
        const transaction = await getTransactionByAppointment(clinicId, apt.id);
        if (transaction) {
          await updateTransaction({
            clinicId,
            transactionId: transaction.id,
            updateData: { status: TRANSACTION_STATUS.PENDING_COLLECTION },
            therapistId,
            therapistName,
            patientName: apt.patientName,
            patientId: apt.patientId,
          }).unwrap();
          invalidateAllFinanceTags(apt.patientId);
        }
      } catch (txError) {
        console.error("[PAG-001] Transaction error:", txError);
      }

      updateCardState({ isLoading: false });
    } catch (error) {
      console.error("[PAG-002] Approval error:", error);
      updateCardState({ isLoading: false });
    }
  };

  // Approval: No - show confirmation
  const handleApprovalNo = (apt) => {
    updateCardState({ currentStep: STEPS.APPROVAL_CANCEL_CONFIRM });
  };

  // Approval: Cancel confirmed
  const handleApprovalCancelConfirm = async (apt) => {
    updateCardState({ isLoading: true });
    try {
      await updateAppointment({
        clinicId,
        appointmentId: apt.id,
        appointmentData: {
          status: "cancelled",
          isApprovedByTherapist: true,
          NotifyAboutThisMeetingApproval: false,
          NotifyAboutThisMeetingPaymentStatus: false,
          NotifyAboutThisMeetingSummarize: false,
        },
        therapistId,
        therapistName,
        patientName: apt.patientName,
      }).unwrap();

      try {
        const transaction = await getTransactionByAppointment(clinicId, apt.id);
        if (transaction) {
          await updateTransaction({
            clinicId,
            transactionId: transaction.id,
            updateData: { status: TRANSACTION_STATUS.CANCELLED },
            therapistId,
            therapistName,
            patientName: apt.patientName,
            patientId: apt.patientId,
          }).unwrap();
          invalidateAllFinanceTags(apt.patientId);
        }
      } catch (txError) {
        console.error("[PAG-003] Transaction error:", txError);
      }

      // Ask about cancellation fee
      updateCardState({
        isLoading: false,
        currentStep: STEPS.CANCELLATION_PAYMENT_ASK,
      });
    } catch (error) {
      console.error("[PAG-004] Cancel error:", error);
      updateCardState({ isLoading: false });
    }
  };

  const handleApprovalCancelBack = (apt) => {
    updateCardState({ currentStep: null });
  };

  // Cancellation payment flow
  const handleCancellationPaymentYes = (apt) => {
    updateCardState({
      currentStep: STEPS.CANCELLATION_PAYMENT_AMOUNT,
      paymentAmount: apt.paymentAmount ?? "",
    });
  };

  const handleCancellationPaymentNo = (apt) => {
    updateCardState({ currentStep: null });
    markResolved();
  };

  const handleCancellationPaymentAmountConfirm = (apt) => {
    const appointmentDate = new Date(apt.startTime);
    updateCardState({
      currentStep: STEPS.CANCELLATION_PAYMENT_DETAILS,
      paymentDate: getIsraelDateString(appointmentDate),
      paymentMethod: "cash",
    });
  };

  const handleCancellationPaymentDetailsConfirm = async (apt) => {
    updateCardState({ isLoading: true });
    const cardState = getCardState();
    const paymentAmount = cardState.paymentAmount
      ? parseFloat(cardState.paymentAmount)
      : apt.paymentAmount;
    const paymentMethod = cardState.paymentMethod || "cash";
    const paymentDate = cardState.paymentDate
      ? new Date(cardState.paymentDate).toISOString()
      : new Date().toISOString();

    try {
      await updateAppointment({
        clinicId,
        appointmentId: apt.id,
        appointmentData: {
          isTherapistUpdatedPaymentStatus: true,
          paymentStatus: "paid",
          paymentAmount,
          actualPaidAmount: paymentAmount,
          paymentMethod,
          paymentDate,
          cancellationFeeRecorded: true,
        },
        therapistId,
        therapistName,
        patientName: apt.patientName,
      }).unwrap();

      try {
        const transaction = await getTransactionByAppointment(clinicId, apt.id);
        if (transaction) {
          await updateTransaction({
            clinicId,
            transactionId: transaction.id,
            updateData: {
              status: TRANSACTION_STATUS.PAID,
              amount: paymentAmount,
              paymentMethod,
              actualPaymentDate: paymentDate,
              isCancellationFee: true,
            },
            therapistId,
            therapistName,
            patientName: apt.patientName,
            patientId: apt.patientId,
          }).unwrap();
          invalidateAllFinanceTags(apt.patientId);
        }
      } catch (txError) {
        console.error("[PAG-005] Transaction error:", txError);
      }

      if (isUsingGreenInvoice) {
        updateCardState({
          isLoading: false,
          currentStep: STEPS.CANCELLATION_PAYMENT_INVOICE,
          paymentAmount,
          paymentMethod,
        });
      } else {
        updateCardState({ isLoading: false, currentStep: null });
        markResolved();
      }
    } catch (error) {
      console.error("[PAG-006] Cancellation payment error:", error);
      updateCardState({ isLoading: false });
    }
  };

  const handleCancellationInvoiceYes = (apt) => {
    const cardState = getCardState();
    setInvoiceAppointment({
      ...apt,
      paymentMethod: cardState.paymentMethod ?? apt.paymentMethod ?? "cash",
    });
    setInvoicePaidAmount(cardState.paymentAmount ?? apt.actualPaidAmount);
    setShowInvoiceModal(true);
    updateCardState({ currentStep: null });
    markResolved();
  };

  const handleCancellationInvoiceNo = (apt) => {
    updateCardState({ currentStep: null });
    markResolved();
  };

  // Payment: Confirm (with details)
  const handlePaymentYes = async (apt, amount, method, date) => {
    updateCardState({ isLoading: true });
    const paymentAmount = amount ? parseFloat(amount) : apt.paymentAmount;
    const paymentMethod = method || "cash";
    const paymentDate = date
      ? new Date(date).toISOString()
      : new Date().toISOString();

    try {
      await updateAppointment({
        clinicId,
        appointmentId: apt.id,
        appointmentData: {
          isTherapistUpdatedPaymentStatus: true,
          NotifyAboutThisMeetingPaymentStatus: false,
          paymentStatus: "paid",
          paymentAmount,
          actualPaidAmount: paymentAmount,
          paymentMethod,
          paymentDate,
        },
        therapistId,
        therapistName,
        patientName: apt.patientName,
      }).unwrap();

      try {
        const transaction = await getTransactionByAppointment(clinicId, apt.id);
        if (transaction) {
          await updateTransaction({
            clinicId,
            transactionId: transaction.id,
            updateData: {
              status: TRANSACTION_STATUS.PAID,
              amount: paymentAmount,
              paymentMethod,
              actualPaymentDate: paymentDate,
            },
            therapistId,
            therapistName,
            patientName: apt.patientName,
            patientId: apt.patientId,
          }).unwrap();
          invalidateAllFinanceTags(apt.patientId);
        }
      } catch (txError) {
        console.error("[PAG-007] Transaction error:", txError);
      }

      if (isUsingGreenInvoice) {
        updateCardState({
          isLoading: false,
          currentStep: STEPS.PAYMENT_INVOICE,
          paymentAmount,
          paymentMethod,
        });
      } else {
        updateCardState({ isLoading: false, currentStep: null });
      }
    } catch (error) {
      console.error("[PAG-008] Payment error:", error);
      updateCardState({ isLoading: false });
    }
  };

  // Payment: No - show task question
  const handlePaymentNo = (apt) => {
    const cardState = getCardState();
    updateCardState({
      currentStep: STEPS.PAYMENT_TASK,
      paymentAmount: cardState.paymentAmount ?? apt.paymentAmount,
    });
  };

  // Payment task: Yes
  const handlePaymentTaskYes = async (apt) => {
    updateCardState({ isLoading: true });
    const cardState = getCardState();
    const enteredAmount = cardState.paymentAmount ?? apt.paymentAmount;
    const paymentAmount = enteredAmount ? parseFloat(enteredAmount) : null;

    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 6);

    const appointmentDate = new Date(apt.startTime);
    const dateStr = appointmentDate.toLocaleDateString("he-IL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

    const amountStr = paymentAmount
      ? `₪${paymentAmount.toLocaleString("he-IL")}`
      : "";
    const taskTitle = paymentAmount
      ? `לגבות תשלום ${amountStr} מ${apt.patientName} (פגישה מתאריך ${dateStr})`
      : `לגבות תשלום מ${apt.patientName} (פגישה מתאריך ${dateStr})`;

    try {
      await createTask({
        clinicId,
        taskData: {
          title: taskTitle,
          description: `תזכורת לגביית תשלום${
            amountStr ? ` בסך ${amountStr}` : ""
          } מ${apt.patientName}`,
          dueDate: dueDate.toISOString(),
          status: "todo",
          priority: "medium",
          scope: "personal",
          assignedTo: therapistId,
          relatedPatientId: apt.patientId,
          relatedAppointmentId: apt.id,
        },
        creatorId: therapistId,
        creatorName: therapistName,
      }).unwrap();

      const updateData = {
        isTherapistUpdatedPaymentStatus: true,
        NotifyAboutThisMeetingPaymentStatus: false,
        paymentStatus: "debt",
      };
      if (paymentAmount !== null) updateData.paymentAmount = paymentAmount;

      await updateAppointment({
        clinicId,
        appointmentId: apt.id,
        appointmentData: updateData,
        therapistId,
        therapistName,
        patientName: apt.patientName,
      }).unwrap();

      if (paymentAmount !== null) {
        try {
          const transaction = await getTransactionByAppointment(
            clinicId,
            apt.id
          );
          if (transaction) {
            await updateTransaction({
              clinicId,
              transactionId: transaction.id,
              updateData: {
                amount: paymentAmount,
                status: TRANSACTION_STATUS.PENDING_COLLECTION,
              },
              therapistId,
              therapistName,
              patientName: apt.patientName,
              patientId: apt.patientId,
            }).unwrap();
            invalidateAllFinanceTags(apt.patientId);
          }
        } catch (txError) {
          console.error("[PAG-009] Transaction error:", txError);
        }
      }

      updateCardState({ isLoading: false, currentStep: null });
    } catch (error) {
      console.error("[PAG-010] Task creation error:", error);
      updateCardState({ isLoading: false });
    }
  };

  // Payment task: No
  const handlePaymentTaskNo = async (apt) => {
    updateCardState({ isLoading: true });
    const cardState = getCardState();
    const enteredAmount = cardState.paymentAmount ?? apt.paymentAmount;
    const paymentAmount = enteredAmount ? parseFloat(enteredAmount) : null;

    try {
      const updateData = {
        isTherapistUpdatedPaymentStatus: true,
        NotifyAboutThisMeetingPaymentStatus: false,
        paymentStatus: paymentAmount !== null ? "debt" : "skipped",
      };
      if (paymentAmount !== null) updateData.paymentAmount = paymentAmount;

      await updateAppointment({
        clinicId,
        appointmentId: apt.id,
        appointmentData: updateData,
        therapistId,
        therapistName,
        patientName: apt.patientName,
      }).unwrap();

      if (paymentAmount !== null) {
        try {
          const transaction = await getTransactionByAppointment(
            clinicId,
            apt.id
          );
          if (transaction) {
            await updateTransaction({
              clinicId,
              transactionId: transaction.id,
              updateData: {
                amount: paymentAmount,
                status: TRANSACTION_STATUS.PENDING_COLLECTION,
              },
              therapistId,
              therapistName,
              patientName: apt.patientName,
              patientId: apt.patientId,
            }).unwrap();
            invalidateAllFinanceTags(apt.patientId);
          }
        } catch (txError) {
          console.error("[PAG-011] Transaction error:", txError);
        }
      }

      updateCardState({ isLoading: false, currentStep: null });
    } catch (error) {
      console.error("[PAG-012] Skip payment error:", error);
      updateCardState({ isLoading: false });
    }
  };

  // Invoice handlers
  const handleInvoiceYes = (apt) => {
    const cardState = getCardState();
    setInvoiceAppointment({
      ...apt,
      paymentMethod: cardState.paymentMethod ?? apt.paymentMethod ?? "cash",
    });
    setInvoicePaidAmount(cardState.paymentAmount ?? apt.actualPaidAmount);
    setShowInvoiceModal(true);
    updateCardState({ currentStep: null });
  };

  const handleInvoiceNo = (apt) => {
    updateCardState({ currentStep: null });
  };

  const handleInvoiceSuccess = async (invoiceData) => {
    const appointmentsToUpdate =
      invoiceData.appointments ||
      (invoiceAppointment ? [invoiceAppointment] : []);

    for (const apt of appointmentsToUpdate) {
      try {
        // Update the appointment with invoice data
        await updateAppointment({
          clinicId,
          appointmentId: apt.id,
          appointmentData: {
            isProducedReceipitForThisMeeting: true,
            RecepitsDocsRefArray: [
              ...(apt.RecepitsDocsRefArray || []),
              {
                documentId: invoiceData.documentId,
                invoiceNumber: invoiceData.invoiceNumber,
                invoiceUrl: invoiceData.invoiceUrl,
                createdAt: new Date().toISOString(),
              },
            ],
          },
          therapistId,
          therapistName,
          patientName: apt.patientName,
        }).unwrap();

        // Also update the transaction status to INVOICE_AUTO
        try {
          const transaction = await getTransactionByAppointment(clinicId, apt.id);
          if (transaction) {
            await updateTransactionStatus({
              clinicId,
              transactionId: transaction.id,
              newStatus: TRANSACTION_STATUS.INVOICE_AUTO,
              additionalData: {
                invoiceNumber: invoiceData.invoiceNumber,
                invoiceUrl: invoiceData.invoiceUrl,
                invoiceDocumentId: invoiceData.documentId,
              },
              therapistId,
              therapistName,
              patientName: apt.patientName,
              patientId: apt.patientId,
            }).unwrap();
          }
        } catch (txError) {
          console.error("[PAG-013b] Transaction status update error:", txError);
        }
      } catch (error) {
        console.error("[PAG-013] Invoice link error:", error);
      }
    }
  };

  const handleInvoiceModalClose = () => {
    setShowInvoiceModal(false);
    setInvoiceAppointment(null);
    setInvoicePaidAmount(null);
  };

  // Summary: Yes
  const handleSummaryYes = (apt) => {
    // Temp customers have no patient record — no treatment doc to navigate to
    if (!apt.patientId) return;
    navigate(
      `/dashboard/patients/${apt.patientId}/treatments/new?appointmentId=${apt.id}`
    );
  };

  // Summary: No - show reminder question
  const handleSummaryNo = (apt) => {
    updateCardState({ currentStep: STEPS.SUMMARY_REMINDER });
  };

  // Summary reminder: Yes
  const handleSummaryReminderYes = async (apt) => {
    updateCardState({ isLoading: true });
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 1);

    const appointmentDate = new Date(apt.startTime);
    const dateStr = appointmentDate.toLocaleDateString("he-IL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const timeStr = appointmentDate.toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });

    try {
      await createTask({
        clinicId,
        taskData: {
          title: `לתעד סיכום פגישה עם ${apt.patientName} (${dateStr}, ${timeStr})`,
          description: `תזכורת לכתוב סיכום טיפול עבור הפגישה`,
          dueDate: dueDate.toISOString(),
          status: "todo",
          priority: "medium",
          scope: "personal",
          assignedTo: therapistId,
          relatedPatientId: apt.patientId,
          relatedAppointmentId: apt.id,
        },
        creatorId: therapistId,
        creatorName: therapistName,
      }).unwrap();

      await updateAppointment({
        clinicId,
        appointmentId: apt.id,
        appointmentData: {
          isTherapistSummarizedTheMeeting: true,
          NotifyAboutThisMeetingSummarize: false,
        },
        therapistId,
        therapistName,
        patientName: apt.patientName,
      }).unwrap();

      updateCardState({ isLoading: false, currentStep: null });
    } catch (error) {
      console.error("[PAG-014] Summary reminder error:", error);
      updateCardState({ isLoading: false });
    }
  };

  // Summary reminder: No
  const handleSummaryReminderNo = async (apt) => {
    updateCardState({ isLoading: true });
    try {
      await updateAppointment({
        clinicId,
        appointmentId: apt.id,
        appointmentData: {
          isTherapistSummarizedTheMeeting: true,
          NotifyAboutThisMeetingSummarize: false,
        },
        therapistId,
        therapistName,
        patientName: apt.patientName,
      }).unwrap();
      updateCardState({ isLoading: false, currentStep: null });
    } catch (error) {
      console.error("[PAG-015] Skip summary error:", error);
      updateCardState({ isLoading: false });
    }
  };

  // ==========================================
  // RENDER
  // ==========================================

  // Clean mini-stepper showing the meeting's phases (approval / summary / payment).
  // A phase is shown if it is pending OR already done, giving a clear sense of
  // progress. Only rendered when 2+ phases are involved.
  const renderStepper = (apt, currentStep) => {
    const phases = [
      {
        key: "approval",
        show:
          apt.NotifyAboutThisMeetingApproval === true ||
          apt.isApprovedByTherapist === true,
        done: apt.isApprovedByTherapist === true,
        active:
          currentStep === STEPS.APPROVAL ||
          currentStep === STEPS.APPROVAL_CANCEL_CONFIRM ||
          currentStep === STEPS.CANCELLATION_PAYMENT_ASK ||
          currentStep === STEPS.CANCELLATION_PAYMENT_AMOUNT ||
          currentStep === STEPS.CANCELLATION_PAYMENT_DETAILS ||
          currentStep === STEPS.CANCELLATION_PAYMENT_INVOICE,
        label: "אישור",
        icon: <Clock size={13} weight="fill" />,
      },
      {
        key: "summary",
        show:
          apt.isTempCustomer !== true &&
          (apt.NotifyAboutThisMeetingSummarize === true ||
            apt.isTherapistSummarizedTheMeeting === true),
        done: apt.isTherapistSummarizedTheMeeting === true,
        active:
          currentStep === STEPS.SUMMARY || currentStep === STEPS.SUMMARY_REMINDER,
        label: "סיכום",
        icon: <NotePencil size={13} weight="fill" />,
      },
      {
        key: "payment",
        show:
          canAccessFinance &&
          (apt.NotifyAboutThisMeetingPaymentStatus === true ||
            apt.isTherapistUpdatedPaymentStatus === true),
        done: apt.isTherapistUpdatedPaymentStatus === true,
        active:
          currentStep === STEPS.PAYMENT ||
          currentStep === STEPS.PAYMENT_DETAILS ||
          currentStep === STEPS.PAYMENT_TASK ||
          currentStep === STEPS.PAYMENT_INVOICE,
        label: "תשלום",
        icon: <CurrencyCircleDollar size={13} weight="fill" />,
      },
    ];

    const shown = phases.filter((p) => p.show);
    if (shown.length < 2) return null;

    return (
      <div className="cu-stepper">
        {shown.map((p, i) => (
          <Fragment key={p.key}>
            {i > 0 && <span className="cu-stepper-sep" />}
            <span
              className={`cu-stepper-item ${p.done ? "done" : ""} ${
                p.active ? "active" : ""
              } ${poppingSteps[p.key] ? "pop" : ""}`}
            >
              {p.done ? <Check size={13} weight="bold" /> : p.icon}
              <span>{p.label}</span>
            </span>
          </Fragment>
        ))}
      </div>
    );
  };

  // Render the body + actions for the current step (clean CATCH UP design).
  const renderStepContent = (apt, currentStep, cardState) => {
    if (cardState.isLoading) {
      return (
        <div className="cu-loading">
          <LoaderLoop size="sm" />
          <span>מעדכן...</span>
        </div>
      );
    }

    // Approval — did the meeting happen?
    if (currentStep === STEPS.APPROVAL) {
      return (
        <>
          <div className="cu-body">
            <h3 className="cu-body-title">האם הפגישה התקיימה?</h3>
            <p className="cu-body-desc">{g("אשר שהפגישה אכן התרחשה כמתוכנן.", "אשרי שהפגישה אכן התרחשה כמתוכנן.")}</p>
          </div>
          <div className="cu-actions">
            <button className="cu-btn cu-btn-yes" onClick={() => handleApprovalYes(apt)}>
              <Check size={18} weight="bold" />
              <span>כן, התקיימה</span>
            </button>
            <button className="cu-btn cu-btn-no" onClick={() => handleApprovalNo(apt)}>
              <X size={18} weight="bold" />
              <span>לא התקיימה</span>
            </button>
          </div>
        </>
      );
    }

    // Approval cancel confirm
    if (currentStep === STEPS.APPROVAL_CANCEL_CONFIRM) {
      return (
        <>
          <div className="cu-body warning">
            <div className="cu-warning-banner">
              <Warning size={18} weight="fill" />
              <span>הפגישה תסומן כבוטלה</span>
            </div>
            <h3 className="cu-body-title">לבטל את הפגישה?</h3>
            <p className="cu-body-desc">הפגישה תסומן כלא התקיימה. אפשר לתעד דמי ביטול בשלב הבא.</p>
          </div>
          <div className="cu-actions">
            <button className="cu-btn cu-btn-danger" onClick={() => handleApprovalCancelConfirm(apt)}>
              <Check size={18} weight="bold" />
              <span>{g("כן, בטל", "כן, בטלי")}</span>
            </button>
            <button className="cu-btn cu-btn-ghost" onClick={() => handleApprovalCancelBack(apt)}>
              <span>חזרה</span>
            </button>
          </div>
        </>
      );
    }

    // Cancellation payment ask
    if (currentStep === STEPS.CANCELLATION_PAYMENT_ASK) {
      return (
        <>
          <div className="cu-body">
            <h3 className="cu-body-title">לתעד דמי ביטול?</h3>
            <p className="cu-body-desc">אם נגבו דמי ביטול עבור הפגישה, ניתן לתעד אותם כעת.</p>
          </div>
          <div className="cu-actions">
            <button className="cu-btn cu-btn-yes" onClick={() => handleCancellationPaymentYes(apt)}>
              <Check size={18} weight="bold" />
              <span>כן</span>
            </button>
            <button className="cu-btn cu-btn-no" onClick={() => handleCancellationPaymentNo(apt)}>
              <X size={18} weight="bold" />
              <span>לא</span>
            </button>
          </div>
        </>
      );
    }

    // Cancellation payment amount
    if (currentStep === STEPS.CANCELLATION_PAYMENT_AMOUNT) {
      return (
        <>
          <div className="cu-body">
            <h3 className="cu-body-title">סכום דמי הביטול</h3>
            <div className="cu-amount">
              <span className="cu-amount-currency">₪</span>
              <input
                type="number"
                inputMode="decimal"
                placeholder="0"
                value={cardState.paymentAmount ?? ""}
                onChange={(e) => updateCardState({ paymentAmount: e.target.value })}
              />
            </div>
          </div>
          <div className="cu-actions">
            <button className="cu-btn cu-btn-yes" onClick={() => handleCancellationPaymentAmountConfirm(apt)}>
              <span>{g("המשך", "המשיכי")}</span>
            </button>
            <button className="cu-btn cu-btn-no" onClick={() => handleCancellationPaymentNo(apt)}>
              <span>ביטול</span>
            </button>
          </div>
        </>
      );
    }

    // Cancellation payment details
    if (currentStep === STEPS.CANCELLATION_PAYMENT_DETAILS) {
      return (
        <>
          <div className="cu-body">
            <h3 className="cu-body-title">פרטי דמי הביטול</h3>
            <div className="cu-field">
              <span className="cu-field-label">תאריך</span>
              <DatePicker
                value={cardState.paymentDate || ""}
                onChange={(e) => updateCardState({ paymentDate: e.target.value })}
              />
            </div>
            <div className="cu-field">
              <span className="cu-field-label">אמצעי תשלום</span>
              <div className="cu-methods">
                {PAYMENT_METHODS.map((m) => (
                  <button
                    key={m.value}
                    className={`cu-method ${cardState.paymentMethod === m.value ? "active" : ""}`}
                    onClick={() => updateCardState({ paymentMethod: m.value })}
                  >
                    {m.label}
                    {m.subLabel && <span className="cu-method-sub">{m.subLabel}</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="cu-actions">
            <button className="cu-btn cu-btn-yes" onClick={() => handleCancellationPaymentDetailsConfirm(apt)}>
              <Check size={18} weight="bold" />
              <span>שמירה</span>
            </button>
            <button className="cu-btn cu-btn-no" onClick={() => handleCancellationPaymentNo(apt)}>
              <span>ביטול</span>
            </button>
          </div>
        </>
      );
    }

    // Cancellation invoice
    if (currentStep === STEPS.CANCELLATION_PAYMENT_INVOICE) {
      return (
        <>
          <div className="cu-body">
            <h3 className="cu-body-title">להפיק חשבונית לדמי הביטול?</h3>
          </div>
          <div className="cu-actions">
            <button className="cu-btn cu-btn-yes" onClick={() => handleCancellationInvoiceYes(apt)}>
              <Receipt size={18} weight="bold" />
              <span>{g("כן, הפק", "כן, הפיקי")}</span>
            </button>
            <button className="cu-btn cu-btn-no" onClick={() => handleCancellationInvoiceNo(apt)}>
              <span>{g("דלג", "דלגי")}</span>
            </button>
          </div>
        </>
      );
    }

    // Payment — amount
    if (currentStep === STEPS.PAYMENT) {
      const effectiveAmount = cardState.paymentAmount ?? apt.paymentAmount;
      const hasValidAmount = effectiveAmount && parseFloat(effectiveAmount) > 0;
      return (
        <>
          <div className="cu-body">
            <h3 className="cu-body-title">האם התקבל תשלום?</h3>
            <p className="cu-body-desc">{g("הזן את סכום התשלום עבור הפגישה.", "הזיני את סכום התשלום עבור הפגישה.")}</p>
            <div className="cu-amount">
              <span className="cu-amount-currency">₪</span>
              <input
                type="number"
                inputMode="decimal"
                placeholder={apt.paymentAmount || "0"}
                value={cardState.paymentAmount ?? ""}
                onChange={(e) => updateCardState({ paymentAmount: e.target.value })}
              />
            </div>
          </div>
          <div className="cu-actions">
            <button
              className="cu-btn cu-btn-yes"
              disabled={!hasValidAmount}
              onClick={() => {
                const appointmentDate = new Date(apt.startTime);
                updateCardState({
                  currentStep: STEPS.PAYMENT_DETAILS,
                  paymentAmount: cardState.paymentAmount ?? apt.paymentAmount,
                  paymentDate: getIsraelDateString(appointmentDate),
                  paymentMethod: "cash",
                });
              }}
            >
              <Check size={18} weight="bold" />
              <span>{g("כן, המשך", "כן, המשיכי")}</span>
            </button>
            <button className="cu-btn cu-btn-no" onClick={() => handlePaymentNo(apt)}>
              <X size={18} weight="bold" />
              <span>לא שולם</span>
            </button>
          </div>
        </>
      );
    }

    // Payment details
    if (currentStep === STEPS.PAYMENT_DETAILS) {
      return (
        <>
          <div className="cu-body">
            <h3 className="cu-body-title">פרטי התשלום</h3>
            <div className="cu-field">
              <span className="cu-field-label">תאריך קבלה</span>
              <DatePicker
                value={cardState.paymentDate || ""}
                onChange={(e) => updateCardState({ paymentDate: e.target.value })}
              />
            </div>
            <div className="cu-field">
              <span className="cu-field-label">אמצעי תשלום</span>
              <div className="cu-methods">
                {PAYMENT_METHODS.map((m) => (
                  <button
                    key={m.value}
                    className={`cu-method ${cardState.paymentMethod === m.value ? "active" : ""}`}
                    onClick={() => updateCardState({ paymentMethod: m.value })}
                  >
                    {m.label}
                    {m.subLabel && <span className="cu-method-sub">{m.subLabel}</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="cu-actions">
            <button
              className="cu-btn cu-btn-yes"
              onClick={() =>
                handlePaymentYes(
                  apt,
                  cardState.paymentAmount,
                  cardState.paymentMethod,
                  cardState.paymentDate
                )
              }
            >
              <Check size={18} weight="bold" />
              <span>שמירת תשלום</span>
            </button>
            <button className="cu-btn cu-btn-no" onClick={() => updateCardState({ currentStep: null })}>
              <span>חזרה</span>
            </button>
          </div>
        </>
      );
    }

    // Payment task question
    if (currentStep === STEPS.PAYMENT_TASK) {
      return (
        <>
          <div className="cu-body">
            <h3 className="cu-body-title">ליצור משימת תזכורת לגבייה?</h3>
            <p className="cu-body-desc">אם לא, התשלום עדיין יופיע בעמוד "פיננסים ותשלומים".</p>
          </div>
          <div className="cu-actions">
            <button className="cu-btn cu-btn-yes" onClick={() => handlePaymentTaskYes(apt)}>
              <Check size={18} weight="bold" />
              <span>{g("כן, צור משימה", "כן, צרי משימה")}</span>
            </button>
            <button className="cu-btn cu-btn-no" onClick={() => handlePaymentTaskNo(apt)}>
              <span>לא, תודה</span>
            </button>
          </div>
        </>
      );
    }

    // Payment invoice
    if (currentStep === STEPS.PAYMENT_INVOICE) {
      return (
        <>
          <div className="cu-body">
            <h3 className="cu-body-title">להפיק חשבונית עבור התשלום?</h3>
          </div>
          <div className="cu-actions">
            <button className="cu-btn cu-btn-yes" onClick={() => handleInvoiceYes(apt)}>
              <Receipt size={18} weight="bold" />
              <span>{g("כן, הפק", "כן, הפיקי")}</span>
            </button>
            <button className="cu-btn cu-btn-no" onClick={() => handleInvoiceNo(apt)}>
              <span>{g("דלג", "דלגי")}</span>
            </button>
          </div>
        </>
      );
    }

    // Summary
    if (currentStep === STEPS.SUMMARY) {
      return (
        <>
          <div className="cu-body">
            <h3 className="cu-body-title">להוסיף סיכום פגישה?</h3>
            <p className="cu-body-desc">תיעוד קצר של מהלך הפגישה עבור המטופל.</p>
          </div>
          <div className="cu-actions">
            {canWriteTreatments !== false && (
              <button className="cu-btn cu-btn-yes" onClick={() => handleSummaryYes(apt)}>
                <NotePencil size={18} weight="bold" />
                <span>כתיבת סיכום</span>
              </button>
            )}
            <button className="cu-btn cu-btn-no" onClick={() => handleSummaryNo(apt)}>
              <span>לא עכשיו</span>
            </button>
          </div>
        </>
      );
    }

    // Summary reminder
    if (currentStep === STEPS.SUMMARY_REMINDER) {
      return (
        <>
          <div className="cu-body">
            <h3 className="cu-body-title">ליצור תזכורת לכתיבת סיכום?</h3>
          </div>
          <div className="cu-actions">
            <button className="cu-btn cu-btn-yes" onClick={() => handleSummaryReminderYes(apt)}>
              <Check size={18} weight="bold" />
              <span>{g("כן, צור תזכורת", "כן, צרי תזכורת")}</span>
            </button>
            <button className="cu-btn cu-btn-no" onClick={() => handleSummaryReminderNo(apt)}>
              <span>לא, תודה</span>
            </button>
          </div>
        </>
      );
    }

    // No remaining steps - this card has been resolved (signaled via onResolved).
    return null;
  };

  return (
    <div className="cu-card meeting">
      <div className="cu-card-head">
        <CalendarBlank size={15} weight="fill" />
        <span>פגישה</span>
      </div>

      <div className="cu-meeting-id">
        <div className="cu-avatar meeting lg">
          {apt.patientName ? apt.patientName.charAt(0) : "?"}
        </div>
        <div className="cu-id-name">{apt.patientName}</div>
        <div className="cu-id-meta">
          {formatDate(apt.startTime)} · {formatTime(apt.startTime)}
        </div>
      </div>

      {renderStepper(apt, currentStep)}

      {renderStepContent(apt, currentStep, cardState)}

      {isDigitalInvoiceActive ? (
        <DigitalInvoiceQuickModal
          isOpen={showInvoiceModal}
          onClose={handleInvoiceModalClose}
          appointment={invoiceAppointment}
          patientName={invoiceAppointment?.patientName}
          amount={invoicePaidAmount}
          paymentMethod={invoiceAppointment?.paymentMethod}
          onSuccess={handleInvoiceSuccess}
        />
      ) : (
        <InvoiceQuickModal
          isOpen={showInvoiceModal}
          onClose={handleInvoiceModalClose}
          appointment={invoiceAppointment}
          patientName={invoiceAppointment?.patientName}
          amount={invoicePaidAmount}
          paymentMethod={invoiceAppointment?.paymentMethod}
          onSuccess={handleInvoiceSuccess}
        />
      )}
    </div>
  );
}
